export type InfeasibilitySeverity = "ok" | "warning" | "blocking";

export interface ViolationSample {
  node_id: string;
  details: Record<string, unknown>;
  slack: number;
}

export interface PreflightIssue {
  reason_code: string;
  severity: "hard" | "blocking";
  evidence: Record<string, unknown>;
  human_message_ko: string;
  fix_suggestions_ko: string[];
}

export interface Infeasibility {
  severity: InfeasibilitySeverity;
  summary_message_ko: string;
  preflight_issues: PreflightIssue[];
  applied_relaxations: string[];
  fix_suggestions_ko: string[];
  violation_summary: Partial<
    Record<
      "grade_min" | "grade_max" | "team_min" | "coverage_min",
      { count: number; samples: ViolationSample[] }
    >
  >;
  last_error_reason: string | null;
}

export function isRecord(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === "object";
}

export function isInfeasibility(value: unknown): value is Infeasibility {
  return (
    isRecord(value) &&
    (value.severity === "ok" ||
      value.severity === "warning" ||
      value.severity === "blocking")
  );
}

export function getInfeasibilityFromResponseBody(value: unknown) {
  if (!isRecord(value)) return null;

  if (isInfeasibility(value.infeasibility)) return value.infeasibility;

  const detail = value.detail;
  if (isRecord(detail) && isInfeasibility(detail.infeasibility)) {
    return detail.infeasibility;
  }

  return null;
}

export function getInfeasibilityFromError(error: unknown) {
  if (!isRecord(error)) return null;

  const response = error.response;
  if (!isRecord(response)) return null;

  return getInfeasibilityFromResponseBody(response.data);
}

function normalizePythonDictString(value: string) {
  return value
    .replace(/\bNone\b/g, "null")
    .replace(/\bTrue\b/g, "true")
    .replace(/\bFalse\b/g, "false")
    .replace(/'/g, '"');
}

function extractObjectLikeString(value: string) {
  const startIndex = value.indexOf("{");
  const endIndex = value.lastIndexOf("}");
  if (startIndex < 0 || endIndex <= startIndex) return null;
  return value.slice(startIndex, endIndex + 1);
}

function getFallbackSummary(value: string) {
  const match = /["']summary_message_ko["']\s*:\s*["']([^"']+)["']/.exec(value);
  return match?.[1] ?? "";
}

export function getInfeasibilityFromUnknown(value: unknown) {
  if (isInfeasibility(value)) return value;

  const fromBody = getInfeasibilityFromResponseBody(value);
  if (fromBody) return fromBody;

  if (typeof value !== "string") return null;

  const objectLike = extractObjectLikeString(value);
  if (!objectLike) return null;

  const parseCandidates = [objectLike, normalizePythonDictString(objectLike)];

  for (const candidate of parseCandidates) {
    try {
      const parsed = JSON.parse(candidate) as unknown;
      if (isInfeasibility(parsed)) return parsed;

      const infeasibility = getInfeasibilityFromResponseBody(parsed);
      if (infeasibility) return infeasibility;
    } catch {
      // Try the next serialization form.
    }
  }

  const summaryMessage = getFallbackSummary(objectLike);
  if (!summaryMessage) return null;

  return {
    severity: "blocking",
    summary_message_ko: summaryMessage,
    preflight_issues: [],
    applied_relaxations: [],
    fix_suggestions_ko: [],
    violation_summary: {},
    last_error_reason: value,
  } satisfies Infeasibility;
}
